import { supabase } from '../lib/supabase'
import { periodoAnterior, type Intervalo } from '../utils/periodo'

export type FiltroOrganico = {
  intervalo: Intervalo
  /** '' = todas as marcas visíveis */
  marcaId: string
}

export type DiaOrganico = {
  data: string
  cliques: number
  impressoes: number
  /** soma de posição × impressões — dividir pelas impressões ao agregar */
  posicaoPonderada: number
  sessoes: number
  sessoesEngajadas: number
  novosUsuarios: number
  conversoes: number
  valorConversoes: number
  demo: boolean
}

export type TotaisOrganico = Omit<DiaOrganico, 'data' | 'posicaoPonderada'> & { posicaoMedia: number | null }

export type LinhaBusca = {
  /** a consulta digitada, ou a URL da página */
  chave: string
  marcaId: string
  marca: string
  cliques: number
  impressoes: number
  posicaoMedia: number | null
}

export type DadosOrganico = {
  serie: DiaOrganico[]
  anterior: TotaisOrganico
  consultas: LinhaBusca[]
  paginas: LinhaBusca[]
}

type Bruto = Record<string, unknown>
const num = (v: unknown) => Number(v ?? 0)
const numOuNulo = (v: unknown) => (v == null ? null : Number(v))

async function rpc(fn: string, args: Record<string, unknown>): Promise<Bruto[]> {
  const { data, error } = await supabase.rpc(fn, args)
  if (error) throw new Error(error.message)
  return (data ?? []) as Bruto[]
}

const busca = (chave: 'consulta' | 'pagina') => (r: Bruto): LinhaBusca => ({
  chave: String(r[chave]),
  marcaId: String(r.marca_id),
  marca: String(r.marca),
  cliques: num(r.cliques),
  impressoes: num(r.impressoes),
  posicaoMedia: numOuNulo(r.posicao_media),
})

/** Quantas consultas e páginas o painel lista. */
const LIMITE = 20

/** Tudo que o painel orgânico precisa, em quatro chamadas paralelas, já agregado no banco. */
export async function carregarOrganico(f: FiltroOrganico): Promise<DadosOrganico> {
  const marca = f.marcaId || null
  const noPeriodo = (i: Intervalo) => ({ p_inicio: i.inicio, p_fim: i.fim, p_marca: marca })
  const [serie, anterior, consultas, paginas] = await Promise.all([
    rpc('organico_serie', noPeriodo(f.intervalo)),
    rpc('organico_totais', noPeriodo(periodoAnterior(f.intervalo))),
    rpc('organico_consultas', { ...noPeriodo(f.intervalo), p_limite: LIMITE }),
    rpc('organico_paginas', { ...noPeriodo(f.intervalo), p_limite: LIMITE }),
  ])
  const a = anterior[0] ?? {}
  return {
    serie: serie.map((r) => ({
      data: String(r.data),
      cliques: num(r.cliques),
      impressoes: num(r.impressoes),
      posicaoPonderada: num(r.posicao_ponderada),
      sessoes: num(r.sessoes),
      sessoesEngajadas: num(r.sessoes_engajadas),
      novosUsuarios: num(r.novos_usuarios),
      conversoes: num(r.conversoes),
      valorConversoes: num(r.valor_conversoes),
      demo: Boolean(r.demo),
    })),
    anterior: {
      cliques: num(a.cliques),
      impressoes: num(a.impressoes),
      posicaoMedia: numOuNulo(a.posicao_media),
      sessoes: num(a.sessoes),
      sessoesEngajadas: num(a.sessoes_engajadas),
      novosUsuarios: num(a.novos_usuarios),
      conversoes: num(a.conversoes),
      valorConversoes: num(a.valor_conversoes),
      demo: Boolean(a.demo),
    },
    consultas: consultas.map(busca('consulta')),
    paginas: paginas.map(busca('pagina')),
  }
}
